/**
 * User Management - Admin interface for user CRUD operations
 */

import React, { useState, useEffect } from 'react';
import { AdminService, AdminUser, AdminUserListResponse, BulkActionResult } from '../../services/admin';
import { userService } from '../../services/user';
import type { User } from '../../types';
import { DataTable, Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Alert } from '../../components/ui/Alert';
import { Input } from '../../components/ui/Input';
import { UserActionDropdown } from '../../components/admin/UserActionDropdown';

export function UserManagement() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Modals for individual actions
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [tempPasswordUser, setTempPasswordUser] = useState<AdminUser | null>(null);
  const [tempPasswordExpiresMinutes, setTempPasswordExpiresMinutes] = useState(1440);
  const [generatedTempPassword, setGeneratedTempPassword] = useState<string | null>(null);
  const [generatedTempPasswordExpiresAt, setGeneratedTempPasswordExpiresAt] = useState<string | null>(null);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [changeRoleUser, setChangeRoleUser] = useState<AdminUser | null>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [editProfileUser, setEditProfileUser] = useState<AdminUser | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editCohort, setEditCohort] = useState('');
  const [editAssignedInstructorId, setEditAssignedInstructorId] = useState('');
  const [instructors, setInstructors] = useState<User[]>([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  
  // Results
  const [actionResult, setActionResult] = useState<BulkActionResult | null>(null);
  const [showActionResult, setShowActionResult] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [pagination.current, pagination.pageSize, searchQuery, roleFilter, statusFilter]);

  useEffect(() => {
    userService.getInstructors()
      .then(setInstructors)
      .catch((err) => {
        console.error('Failed to load instructors:', err);
      });
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize
      };
      
      if (searchQuery) params.search = searchQuery;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.is_active = statusFilter === 'active';
      
      const response: AdminUserListResponse = await AdminService.getUserList(params);
      
      setUsers(response.users);
      setPagination(prev => ({ ...prev, total: response.total_count }));
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleUserAction = async (action: string, user: AdminUser) => {
    try {
      setError(null);
      let result: BulkActionResult;
      
      switch (action) {
        case 'generate_temp_password':
          setTempPasswordUser(user);
          setGeneratedTempPassword(null);
          setGeneratedTempPasswordExpiresAt(null);
          setTempPasswordExpiresMinutes(1440);
          setShowTempPasswordModal(true);
          return; // Don't process further, let modal handle it
          
        case 'activate':
          result = await AdminService.activateUser(user.id);
          break;
          
        case 'deactivate':
          result = await AdminService.deactivateUser(user.id);
          break;
          
        case 'change_role':
          setChangeRoleUser(user);
          setShowChangeRoleModal(true);
          return; // Don't process further, let modal handle it

        case 'edit_profile':
          setEditProfileUser(user);
          setEditFirstName(user.first_name || '');
          setEditLastName(user.last_name || '');
          setEditCohort(user.cohort || '');
          setEditAssignedInstructorId(user.assigned_instructor_id ? String(user.assigned_instructor_id) : '');
          setShowEditProfileModal(true);
          return; // Don't process further, let modal handle it
          
        case 'delete':
          setDeleteUser(user);
          setShowDeleteModal(true);
          return; // Don't process further, let modal handle it
          
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      setActionResult(result);
      setShowActionResult(true);
      
      // Refresh users list
      await loadUsers();
      
    } catch (err) {
      console.error('User action failed:', err);
      setError('User action failed. Please try again.');
    }
  };

  const handleChangeRole = async () => {
    if (!changeRoleUser || !newRole) return;
    
    try {
      const result = await AdminService.changeUserRole(changeRoleUser.id, newRole);
      setShowChangeRoleModal(false);
      setChangeRoleUser(null);
      setNewRole('');
      
      setActionResult(result);
      setShowActionResult(true);
      
      // Refresh users list
      await loadUsers();
      
    } catch (err) {
      console.error('Role change failed:', err);
      setError('Role change failed. Please try again.');
    }
  };

  const handleSaveProfile = async () => {
    if (!editProfileUser) return;

    const cleanOptionalText = (value: string) => {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    };

    try {
      setSavingProfile(true);
      setError(null);
      await AdminService.updateUserProfile(editProfileUser.id, {
        first_name: cleanOptionalText(editFirstName),
        last_name: cleanOptionalText(editLastName),
        cohort: cleanOptionalText(editCohort),
        assigned_instructor_id:
          editProfileUser.role === 'learner' && editAssignedInstructorId
            ? Number(editAssignedInstructorId)
            : null,
      });

      setShowEditProfileModal(false);
      setEditProfileUser(null);
      setActionResult({
        action: 'edit_profile',
        results: { success: [editProfileUser.id], errors: [] },
        total_requested: 1,
        successful: 1,
        failed: 0,
      });
      setShowActionResult(true);
      await loadUsers();
    } catch (err) {
      console.error('Profile update failed:', err);
      setError('Profile update failed. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    
    try {
      const result = await AdminService.deleteUser(deleteUser.id);
      setShowDeleteModal(false);
      setDeleteUser(null);
      
      setActionResult(result);
      setShowActionResult(true);
      
      // Refresh users list
      await loadUsers();
      
    } catch (err) {
      console.error('User deletion failed:', err);
      setError('User deletion failed. Please try again.');
    }
  };

  const handleGenerateTempPassword = async () => {
    if (!tempPasswordUser) return;
    
    try {
      setError(null);
      if (tempPasswordExpiresMinutes < 5 || tempPasswordExpiresMinutes > 1440) {
        setError('Temp password expiry must be between 5 and 1440 minutes.');
        return;
      }
      const response = await AdminService.generateTempPassword(
        tempPasswordUser.id,
        tempPasswordExpiresMinutes
      );
      setGeneratedTempPassword(response.temp_password);
      setGeneratedTempPasswordExpiresAt(response.expires_at || null);
    } catch (err) {
      console.error('Temp password generation failed:', err);
      setError('Temp password generation failed. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'instructor': return 'primary';
      case 'learner': return 'success';
      default: return 'default';
    }
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'username',
      title: 'Username',
      sortable: true,
      render: (value, user) => (
        <div>
          <div className="font-medium text-white">{user.display_name || value}</div>
          <div className="text-sm text-gray-400">{value} | {user.email}</div>
          {user.cohort && <div className="text-xs text-gray-500">{user.cohort}</div>}
        </div>
      )
    },
    {
      key: 'role',
      title: 'Role',
      sortable: true,
      render: (value) => (
        <Badge variant={getRoleBadgeVariant(value)} className="capitalize">
          {value}
        </Badge>
      )
    },
    {
      key: 'is_active',
      title: 'Status',
      sortable: true,
      render: (value) => (
        <Badge variant={value ? 'success' : 'danger'}>
          {value ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      key: 'run_count',
      title: 'Labs',
      render: (value, user) => (
        <div className="text-center">
          <div className="text-white font-medium">{user.active_run_count}</div>
          <div className="text-xs text-gray-400">active</div>
          <div className="text-xs text-gray-400">{value} total</div>
        </div>
      ),
      align: 'center'
    },
    {
      key: 'last_login',
      title: 'Last Login',
      render: (value) => (
        <span className="text-gray-300">
          {value ? formatDate(value) : 'Never'}
        </span>
      )
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      render: (value) => (
        <span className="text-gray-300">{formatDate(value)}</span>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, user) => (
        <UserActionDropdown
          user={user}
          onAction={handleUserAction}
        />
      ),
      align: 'right'
    }
  ];


  return (
    <div className="p-8 bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">User Management</h1>
            <p className="text-gray-400 mt-1">
              Manage user accounts, roles, and permissions
            </p>
          </div>
          <Button variant="primary">
            Create User
          </Button>
        </div>

        {error && (
          <Alert
            variant="error"
            title="Error"
            className="mb-6"
          >
            {error}
          </Alert>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="instructor">Instructor</option>
            <option value="learner">Learner</option>
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          <Button
            variant="secondary"
            onClick={() => {
              setSearchQuery('');
              setRoleFilter('');
              setStatusFilter('');
            }}
          >
            Clear Filters
          </Button>
        </div>

        {/* Users Table */}
        <DataTable
          data={users}
          columns={columns}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search users..."
          onSearch={handleSearch}
          pagination={{
            current: pagination.current,
            total: pagination.total,
            pageSize: pagination.pageSize,
            onPageChange: (page) => setPagination(prev => ({ ...prev, current: page })),
            onPageSizeChange: (size) => setPagination(prev => ({ ...prev, pageSize: size, current: 1 }))
          }}
          emptyMessage="No users found"
        />

        {/* Edit Profile Modal */}
        <Modal
          isOpen={showEditProfileModal}
          onClose={() => {
            setShowEditProfileModal(false);
            setEditProfileUser(null);
          }}
          title="Edit User Profile"
          size="lg"
        >
          <div className="space-y-5">
            <div>
              <p className="text-gray-300">
                Update profile details for <strong className="text-white">{editProfileUser?.username}</strong>.
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Names and cohorts are shown in instructor dashboards, learner reports, and admin search.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First name"
                value={editFirstName}
                onChange={(event) => setEditFirstName(event.target.value)}
                placeholder="First name"
                data-testid="edit-first-name"
              />
              <Input
                label="Last name"
                value={editLastName}
                onChange={(event) => setEditLastName(event.target.value)}
                placeholder="Last name"
                data-testid="edit-last-name"
              />
            </div>

            <Input
              label="Cohort"
              value={editCohort}
              onChange={(event) => setEditCohort(event.target.value)}
              placeholder="Class, team, or training group"
              data-testid="edit-cohort"
            />

            {editProfileUser?.role === 'learner' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="edit-assigned-instructor">
                  Assigned instructor
                </label>
                <select
                  id="edit-assigned-instructor"
                  value={editAssignedInstructorId}
                  onChange={(event) => setEditAssignedInstructorId(event.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                  data-testid="edit-assigned-instructor"
                >
                  <option value="">Unassigned</option>
                  {instructors.map((instructor) => (
                    <option key={instructor.id} value={instructor.id}>
                      {instructor.display_name || instructor.username}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                onClick={handleSaveProfile}
                loading={savingProfile}
                data-testid="save-profile"
              >
                Save Profile
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditProfileModal(false);
                  setEditProfileUser(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Change Role Modal */}
        <Modal
          isOpen={showChangeRoleModal}
          onClose={() => {
            setShowChangeRoleModal(false);
            setChangeRoleUser(null);
            setNewRole('');
          }}
          title="Change User Role"
        >
          <div className="space-y-4">
            <p className="text-gray-300">
              Change role for user: <strong>{changeRoleUser?.username}</strong>
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                New Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
                data-testid="role-select"
              >
                <option value="">Select Role</option>
                <option value="admin">Admin</option>
                <option value="instructor">Instructor</option>
                <option value="learner">Learner</option>
              </select>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                onClick={handleChangeRole}
                disabled={!newRole}
                data-testid="confirm-role-change"
              >
                Change Role
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowChangeRoleModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Temp Password Modal */}
        <Modal
          isOpen={showTempPasswordModal}
          onClose={() => {
            setShowTempPasswordModal(false);
            setTempPasswordUser(null);
            setTempPasswordExpiresMinutes(1440);
            setGeneratedTempPassword(null);
            setGeneratedTempPasswordExpiresAt(null);
          }}
          title="Generate Temporary Password"
        >
          <div className="space-y-4">
            <p className="text-gray-300">
              Generate a temporary password for user: <strong>{tempPasswordUser?.username}</strong>
            </p>

            {!generatedTempPassword ? (
              <>
                <div className="text-yellow-400 bg-yellow-900/20 border border-yellow-600 rounded p-3 text-sm">
                  This will invalidate the user's current password and require a change on next login.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Temp password expires in (minutes)
                  </label>
                  <Input
                    type="number"
                    min={5}
                    max={1440}
                    value={tempPasswordExpiresMinutes}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setTempPasswordExpiresMinutes(Number.isNaN(value) ? 60 : value);
                    }}
                    className="bg-gray-800"
                  />
                  <p className="text-xs text-gray-500 mt-1">Min 5, max 1440 (24 hours)</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="primary"
                    onClick={handleGenerateTempPassword}
                  >
                    Generate Temp Password
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setShowTempPasswordModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-gray-800 border border-gray-700 rounded p-4">
                  <div className="text-sm text-gray-400">Temporary password (shown once):</div>
                  <div className="mt-2 font-mono text-white break-all">
                    {generatedTempPassword}
                  </div>
                  {generatedTempPasswordExpiresAt && (
                    <div className="mt-2 text-xs text-gray-400">
                      Expires at: {new Date(generatedTempPasswordExpiresAt).toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="text-gray-400 text-sm">
                  Share this password securely with the user. They will be forced to change it after login.
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="secondary"
                    onClick={() => setShowTempPasswordModal(false)}
                  >
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* Delete User Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteUser(null);
          }}
          title="Confirm Delete User"
        >
          <div className="space-y-4">
            <div className="text-yellow-400 bg-yellow-900/20 border border-yellow-600 rounded p-3">
              <strong>⚠️ Warning:</strong> This action cannot be undone.
            </div>
            
            <p className="text-gray-300">
              Are you sure you want to delete user: <strong className="text-white">{deleteUser?.username}</strong>?
            </p>
            
            <p className="text-gray-400 text-sm">
              Email: {deleteUser?.email}
            </p>
            
            {deleteUser && deleteUser.active_run_count > 0 && (
              <div className="text-red-400 bg-red-900/20 border border-red-600 rounded p-3">
                <strong>Note:</strong> This user has {deleteUser.active_run_count} active lab runs.
                Deletion will fail if they have active runs.
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="danger"
                onClick={handleDeleteUser}
                data-testid="confirm-delete-user"
              >
                Delete User
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Action Result Modal */}
        <Modal
          isOpen={showActionResult}
          onClose={() => {
            setShowActionResult(false);
            setActionResult(null);
          }}
          title="Action Result"
        >
          {actionResult && (
            <div className="space-y-4">
              <div className="text-green-400">
                ✅ {actionResult.successful} of {actionResult.total_requested} actions completed successfully
              </div>
              
              {actionResult.results.errors.length > 0 && (
                <div>
                  <div className="text-red-400 mb-2">
                    ❌ {actionResult.failed} actions failed:
                  </div>
                  <div className="space-y-1 text-sm">
                    {actionResult.results.errors.map((error, index) => (
                      <div key={index} className="text-gray-400">
                        User {error.user_id}: {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <Button
                variant="secondary"
                onClick={() => setShowActionResult(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
